import type { Table } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from "@/lib/utils";

interface DataTablePaginationProps<TData> extends React.ComponentProps<'div'> {
  table: Table<TData>;
  pageSizeOptions?: number[];
}

export function DataTablePagination<TData>({
  table,
  pageSizeOptions = [10, 20, 50, 100, 200],
  className,
  ...props
}: DataTablePaginationProps<TData>) {
  const { t } = useTranslation('common');

  return (
    <div
      className={cn(
        'flex w-full flex-col items-stretch gap-3 p-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4 md:gap-6 lg:gap-8',
        className,
      )}
      {...props}
    >
      <div className="text-muted-foreground text-center text-xs sm:flex-1 sm:text-left sm:text-sm">
        {t('rowsSelected', {
          count: table.getFilteredSelectedRowModel().rows.length,
          total: table.getFilteredRowModel().rows.length,
        })}
      </div>
      <div className="text-muted-foreground hidden text-xs sm:block sm:text-sm">
        {t('itemsInTotal', { count: table.getRowCount() })}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 sm:flex-nowrap sm:gap-4 md:gap-5 lg:gap-6">
        <div className="flex items-center space-x-2">
          <p className="whitespace-nowrap text-xs font-medium sm:text-sm">{t('rowsPerPage')}</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-9 w-[4.5rem] sm:h-8 sm:w-[4rem] md:w-[4.5rem] [&[data-size]]:h-9 sm:[&[data-size]]:h-8">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center whitespace-nowrap text-xs font-medium sm:text-sm">
          {t('page')} {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            aria-label={t('goToFirstPage')}
            variant="outline"
            size="icon"
            className="hidden size-9 sm:size-8 md:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            aria-label={t('goToPreviousPage')}
            variant="outline"
            size="icon"
            className="size-9 sm:size-8"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            aria-label={t('goToNextPage')}
            variant="outline"
            size="icon"
            className="size-9 sm:size-8"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            aria-label={t('goToLastPage')}
            variant="outline"
            size="icon"
            className="hidden size-9 sm:size-8 md:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
